import React, { useState, useEffect } from 'react';
import Geocode from 'react-geocode';
import GoogleMapReact from 'google-map-react';
import { useFormik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { isEmpty } from 'lodash';

import { NEXT_PUBLIC_GOOGLE_MAPS_KEY } from '@/app-configuration';
import Container from '@components/Container';
import LocationRail from '@components/LocationRail';
import { Layout } from '@components';
import { checkEnabled, checkValue } from '@components/OptionalFeature';
import { reqLocationsAction, reqLocationsResetAction } from '@stores/locations/actions';
import { useAllLocationsSelector } from '@stores/locations/selectors';
import { requestNavigation } from '@helpers/navigation';
import { getLanguageId } from '@/helpers/language';
import { formatPostData } from '@/services/postService';
import { PageContext } from '@contexts';
import { apiClient } from '@/http-client';

import styles from './FindADealer.module.scss';
import SearchIcon from '../../public/search-ico.svg';
import GrayMapIcon from '../../public/Gray_Pin.svg';
import LocationCrosshair from '../../public/location-crosshairs-solid.svg';

// Custom Map Icon
const MapIcon = ({ text, key, index, selected, callback, lat, lng }) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const markerPinDefault = index === selected ? <img src="../Orange_Pin.svg" /> : <img src="../Black_Pin.svg" />;

  const [markerPin, setMarkerPin] = useState(markerPinDefault);
  const markerClicked = () => {
    if (isPopoverOpen) {
      setIsPopoverOpen(false);
      setMarkerPin(<img src="../Black_Pin.svg" />);
    } else {
      setIsPopoverOpen(true);
      setMarkerPin(<img src="../Orange_Pin.svg" />);
    }
    callback(lat, lng, index);
  };

  return (
    <div id={`icon-${index}`} onClick={markerClicked} className={styles.Marker}>
      <div className={styles.MarkerCount}>{index + 1}</div>
      {markerPinDefault}
    </div>
  );
};

export default function DealerFinder(data) {
  const dispatch = useDispatch();
  const router = useRouter();
  const { locale } = router;

  const { flow, product, isUsed } = router.query;

  // Pull these from AWS header request | geo target
  const [lat, setLat] = useState(57.5635766);
  const [lng, setLng] = useState(-111.5619342);
  const [zoom, setZoom] = useState(4);
  const [activeMarker, setActiveMarker] = useState(0);
  const [location, setLocation] = useState({
    permissionState: '',
    latitude: 0,
    longitude: 0,
    isPermission: false
  });

  const defaultProps = {
    center: {
      lat: 57.5635766,
      lng: -111.5619342
    },
    zoom: 4
  };

  const mapProps = {
    center: {
      lat: lat,
      lng: lng
    },
    zoom: zoom
  };

  // Setup Google Geo
  Geocode.setApiKey(NEXT_PUBLIC_GOOGLE_MAPS_KEY);
  Geocode.setLanguage('en');
  Geocode.setRegion('CA');

  // TODO: Turn this off for production
  // Geocode.enableDebug();

  useEffect(() => {
    // Dispatch the payload for user request
    // TODO: Switch to use dynamic latlgn
    dispatch(reqLocationsResetAction());
  }, []);

  // Location List returned from State
  const locationsState = useSelector(useAllLocationsSelector);

  const markerClicked = (latitude, longitude, index) => {
    setLat(parseFloat(latitude));
    setLng(parseFloat(longitude));
    setZoom(10);
    setActiveMarker(index);
  };

  const formik = useFormik({
    initialValues: {
      address: ''
    },
    onSubmit: (values) => {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('FIND_A_DEALDER_ADDRESS', values.address);
      }
      Geocode.fromAddress(values.address).then(
        (response) => {
          const result = response.results.find((res) => res.formatted_address.includes('Canada'));
          if (result) {
            const { lat, lng } = result.geometry.location;
            dispatch(reqLocationsAction({ latlng: `${lng} ${lat}` }));
            setLat(parseFloat(lat));
            setLng(parseFloat(lng));
            setZoom(11);
            setActiveMarker(0);
          }
        },
        (error) => {
          console.error(error);
        }
      );
    }
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const address = window.localStorage.getItem('FIND_A_DEALDER_ADDRESS');
      if (address) {
        formik.setFieldValue('address', address);
        formik.handleSubmit();
      }
    }
  }, []);

  // Return map bounds based on list of places
  const getMapBounds = (map, maps, markers) => {
    const bounds = new maps.LatLngBounds();
    markers.forEach((marker) => {
      if (marker.lat && marker.lng) {
        bounds.extend(new maps.LatLng(parseFloat(marker.lat), parseFloat(marker.lng)));
      }
    });
    return bounds;
  };

  // Re-center map when resizing the window
  const bindResizeListener = (map, maps, bounds) => {
    maps.event.addDomListenerOnce(map, 'idle', () => {
      maps.event.addDomListener(window, 'resize', () => {
        map.fitBounds(bounds);
      });
    });
  };

  // Map to bounds
  // Fit map to its bounds after the api is loaded
  const apiIsLoaded = (map, maps, markers) => {
    if (markers) {
      const bounds = getMapBounds(map, maps, markers);
      map.fitBounds(bounds);
      bindResizeListener(map, maps, bounds);
    }
  };

  // get user location by click on locationCrosshair icon
  const GetUserLocation = () => {
    requestNavigation(location, setLocation);
  };
  const shouldSetStateToLoading = location.isPermission !== false;

  useEffect(() => {
    if (shouldSetStateToLoading) {
      dispatch(reqLocationsAction({ latlng: `${location.longitude} ${location.latitude}` }));
      setLat(parseFloat(location.latitude));
      setLng(parseFloat(location.longitude));
      setZoom(11);
      //setActiveMarker(0);
    }
  }, [shouldSetStateToLoading]);

  const dealerInfo = data.data?.DisplayOptions?.find((x) => x.Name === 'Dealer Info');
  const searchInfo = data.data?.DisplayOptions?.find((x) => x.Name === 'Search');

  // NoDealerText
  const showNoDealerText = checkEnabled(dealerInfo.DisplayOptions, 'NoDealerText');
  const noDealerText = checkValue(dealerInfo.DisplayOptions, 'NoDealerText', locale);
  // Description
  const showDescription = checkEnabled(searchInfo.DisplayOptions, 'ShortDescription');
  const description = checkValue(searchInfo.DisplayOptions, 'ShortDescription', locale);

  return (
    <PageContext.Provider value={data?.data}>
      <Layout>
        <Container>
          <div>
            <form onSubmit={formik.handleSubmit} className={styles.LocationForm}>
              <div className={styles.searchWrapper}>
                <label hidden htmlFor="email">
                  Your Address
                </label>
                <input
                  id="address"
                  name="address"
                  type="address"
                  placeholder={locale === 'en' ? 'Postal Code, Address or City' : 'Code postal, adresse ou ville'}
                  onChange={formik.handleChange}
                  value={formik.values.address}
                  className={styles.input}
                />
                <div className={styles.TopContainer}>
                  <button type="submit" className={styles.searchButton}>
                    <SearchIcon />
                  </button>
                  <button type="button" className={styles.LocationCross} onClick={GetUserLocation}>
                    <LocationCrosshair />
                  </button>
                </div>
              </div>
              <div>
                {showDescription && (
                  <span dangerouslySetInnerHTML={{ __html: description }}></span>
                )}
              </div>
            </form>
            <div className={styles.FlexBox}>
              <div className={styles.LeftGrid}>
                {isEmpty(locationsState?.locations) ? (
                  <div className={styles.noLocationsMessage}>
                    <div>
                      <div>
                        <GrayMapIcon />
                      </div>
                      {showNoDealerText ? noDealerText : null}
                    </div>
                  </div>
                ) : (
                  <LocationRail
                    locations={locationsState.locations.slice(0, 3)}
                    callback={markerClicked}
                    selected={activeMarker}
                    flow={flow}
                    productId={product}
                    isUsed={isUsed}
                    data={data.data}
                  />
                )}
              </div>
              <div className={styles.MapContainer}>
                <GoogleMapReact
                  bootstrapURLKeys={{ key: NEXT_PUBLIC_GOOGLE_MAPS_KEY }}
                  defaultCenter={defaultProps.center}
                  defaultZoom={defaultProps.zoom}
                  center={{
                    lat: lat,
                    lng: lng
                  }}
                  zoom={zoom}
                  yesIWantToUseGoogleMapApiInternals={true}
                  onGoogleApiLoaded={({ map, maps }) => apiIsLoaded(map, maps, locationsState.locations)}
                >
                  {!isEmpty(locationsState?.locations) &&
                    locationsState.locations
                      .slice(0, 3)
                      .map((location, index) => (
                        <MapIcon
                          lat={location.lat}
                          lng={location.lng}
                          text={location.dealerName}
                          index={index}
                          selected={activeMarker}
                          key={index}
                          callback={markerClicked}
                        />
                      ))}
                </GoogleMapReact>
              </div>
            </div>
          </div>
        </Container>
      </Layout>
    </PageContext.Provider>
  );
}

export const getServerSideProps = async ({ locale }) => {
  //static Id of find the dealer
  const title = 'find-a-dealer';

  const languageId = getLanguageId(locale);

  let data = {};

  try {
    const { data: post } = await apiClient.get(`/api/Posts/byTitle/${title}`, { params: { languageId } });
    data = formatPostData(post);
  } catch (error) {
    console.error(`Fetching POST data failed: Title = ${title}`);
  }

  return {
    props: {
      data,
      ...(await serverSideTranslations(locale, ['common', 'footer']))
    }
  };
};
